import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { PricingRangeItem } from 'apis/delivery-settings-api/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';

type DeliveryFeeProps = {
  className?: string;
  deliveryBaseFee: number | undefined;
  distanceBasedPricing: PricingRangeItem[] | undefined;
  currency: Currency;
};

const Root = styled.div``;

const Tooltip = styled(Popover)`
  margin-inline-start: 8px;
`;

const HelpIcon = styled(InfoCircleFilled)`
  fill: ${cbSemanticCssVars.textSecondary};
  width: 12px;
`;

const TooltipContent = styled.div`
  ${typographyCss.Body3}
  width: 240px;
  padding: 16px;
`;

const Description = styled.p`
  margin-block: 0 16px;
`;

const Ranges = styled.div`
  display: grid;
  grid-template-columns: minmax(max-content, 2fr) 1fr;
  gap: 4px 8px;
`;

const Distance = styled.span`
  color: ${cbSemanticCssVars.textSecondary};
`;

const Price = styled.span`
  justify-self: flex-end;
  word-break: break-word;
`;

const NotAvailable = styled.span`
  color: ${cbSemanticCssVars.textTertiary};
`;

const DeliveryFee = ({ className, deliveryBaseFee, distanceBasedPricing, currency }: DeliveryFeeProps) => {
  if (deliveryBaseFee === undefined && !distanceBasedPricing) {
    return <Root className={className}>{'-'}</Root>;
  }

  const baseFee = deliveryBaseFee ?? 0;

  if (distanceBasedPricing) {
    const hasMultiplier = distanceBasedPricing.some(step => !!step.basketMultiplierDecimal);

    return (
      <Root className={className}>
        <FormattedMessage id="venue-details.delivery-areas.custom-delivery-fee" tagName="span" />
        {!hasMultiplier && (
          <Tooltip
            disableContentPadding
            placement="top"
            fallbackPlacements={['auto']}
            content={
              <TooltipContent>
                <Description>
                  <FormattedMessage id="venue-details.delivery-areas.custom-delivery-fee-explanation" />
                </Description>
                <Ranges>
                  {distanceBasedPricing.map((step, index) => (
                    <Fragment key={index}>
                      <Distance>
                        {`${step.rangeMinBound} m - ${step.rangeMaxBound === 0 ? '' : `${step.rangeMaxBound} m`} `}
                      </Distance>
                      <Price>
                        {step.pricingFlag === 'not_available' ? (
                          <NotAvailable>
                            <FormattedMessage id="venue-details.delivery-areas.delivery-not-available" />
                          </NotAvailable>
                        ) : (
                          <MonetaryAmount
                            amount={convertMonetaryIntToDecimal(baseFee + step.rangeBaseFee, currency)}
                            currency={currency}
                          />
                        )}
                      </Price>
                    </Fragment>
                  ))}
                </Ranges>
              </TooltipContent>
            }
          >
            <HelpIcon />
          </Tooltip>
        )}
      </Root>
    );
  }

  return (
    <Root className={className}>
      <MonetaryAmount amount={convertMonetaryIntToDecimal(baseFee, currency)} currency={currency} />
    </Root>
  );
};

export default DeliveryFee;
